import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Badge,
  Modal,
  InputGroup,
  Pagination,
  Tabs,
  Tab,
  Card,
} from "react-bootstrap";
import api from "../api";

const DataTable = ({ refreshData, isAdmin }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Subject filter state
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [subjectSelected, setSubjectSelected] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Sorting state
  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Fetch subjects on component mount
  useEffect(() => {
    fetchSubjects();
  }, []);

  // Fetch data when subject, search term, page or sort changes
  useEffect(() => {
    if (isAdmin && selectedSubject) {
      fetchData();
      setSubjectSelected(true);
    }
  }, [
    isAdmin,
    selectedSubject,
    currentPage,
    pageSize,
    sortField,
    sortDirection,
  ]);

  // Fetch all available subjects
  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true);
      setSubjects([]); // Clear existing subjects before loading
      setError(""); // Clear any previous errors

      try {
        // Get subjects directly from the API endpoint with counts
        const subjectsResponse = await api.get(
          "/api/csv-data/subjects_with_counts/"
        );

        if (subjectsResponse.data && Array.isArray(subjectsResponse.data)) {
          // Map the response to the expected format
          const uniqueSubjects = subjectsResponse.data
            .filter((subject) => subject && subject.subject.trim() !== "")
            .map((item) => ({
              name: item.subject,
              count: item.count,
              isReal: true,
            }));

          // Sort subjects alphabetically
          uniqueSubjects.sort((a, b) => a.name.localeCompare(b.name));

          setSubjects(uniqueSubjects);
          console.log("Subjects loaded with counts:", uniqueSubjects);
        } else {
          // Fallback to using a different approach
          console.log(
            "API didn't return expected format, using fallback method"
          );

          try {
            // Get all questions using pagination with a large page size
            const fallbackResponse = await api.get(
              "/api/csv-data/?page_size=999"
            );

            const questions = fallbackResponse.data?.results || [];
            console.log(`Got ${questions.length} questions`);

            // Extract unique subjects
            const fallbackSubjectCounts = {};
            questions.forEach((item) => {
              if (item.subject) {
                fallbackSubjectCounts[item.subject] =
                  (fallbackSubjectCounts[item.subject] || 0) + 1;
              }
            });

            // Create array for subjects
            const fallbackSubjectsList = Object.keys(fallbackSubjectCounts)
              .filter((subject) => subject && subject.trim() !== "")
              .map((subject) => ({
                name: subject,
                count: fallbackSubjectCounts[subject],
                isReal: true,
              }))
              .sort((a, b) => a.name.localeCompare(b.name));

            if (fallbackSubjectsList.length === 0) {
              console.log("No subjects found, showing empty state");
              setSubjects([
                {
                  name: "No questions found",
                  count: 0,
                  isReal: true,
                  disabled: true,
                },
              ]);
            } else {
              setSubjects(fallbackSubjectsList);
              console.log(
                "Subjects loaded (fallback method):",
                fallbackSubjectsList
              );
            }
          } catch (fallbackErr) {
            console.error("Fallback method failed:", fallbackErr);
            setError("Failed to load subjects. Please try again later.");
            setSubjects([
              {
                name: "Error loading subjects",
                count: 0,
                isReal: true,
                disabled: true,
              },
            ]);
          }
        }
      } catch (err) {
        console.error("Error loading subjects:", err);
        setError(
          "Failed to load subjects. Please refresh the page or try again later."
        );
      }
    } catch (err) {
      console.error("Error in subjects loading:", err);
      setError("Failed to load subjects. Please refresh the page.");
    } finally {
      setLoadingSubjects(false);
    }
  };

  // Fetch data with filters, pagination and sorting
  const fetchData = async () => {
    // Don't fetch data if no subject is selected
    if (!selectedSubject) {
      setData([]);
      setTotalItems(0);
      setTotalPages(0);
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Build query parameters
      const params = new URLSearchParams();

      // Add subject filter
      params.append("subject", selectedSubject);

      // Add search term if provided
      if (searchTerm) {
        params.append("search", searchTerm);
      }

      // Add pagination parameters
      params.append("page", currentPage);
      params.append("page_size", pageSize);

      // Add sorting if specified
      if (sortField) {
        const sortParam = `${sortDirection === "desc" ? "-" : ""}${sortField}`;
        params.append("ordering", sortParam);
      }

      const response = await api.get(`/api/csv-data/?${params}`);

      if (response.data && response.data.results) {
        setData(response.data.results);
        setTotalItems(response.data.count);
        setTotalPages(Math.ceil(response.data.count / pageSize));
      } else {
        console.error("Unexpected API response format:", response.data);
        setData([]);
        setError("Received unexpected data format from the server");
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(
        `Failed to load data. ${err.response?.data?.detail || err.message}`
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle subject filter change
  const handleSubjectChange = (e) => {
    const subject = e.target.value;

    // Check if this is a disabled option (error state)
    const selectedSubjectObj = subjects.find((s) => s.name === subject);
    if (selectedSubjectObj && selectedSubjectObj.disabled) {
      return; // Don't allow selection of disabled options
    }

    setSelectedSubject(subject);
    setCurrentPage(1); // Reset to first page when changing subject

    if (!subject) {
      setSubjectSelected(false);
      setData([]);
      setTotalItems(0);
      setTotalPages(0);
    }
  };

  // Handle clearing all filters
  const handleClearFilters = () => {
    setSearchTerm("");
    setSelectedSubject("");
    setSubjectSelected(false);
    setCurrentPage(1);
    setData([]);
    setTotalItems(0);
    setTotalPages(0);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle search form submission
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when searching
    fetchData();
  };

  // Handle sorting when clicking on column headers
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort indicator for column headers
  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ▲" : " ▼";
  };

  // Edit record handler
  const handleEdit = (record) => {
    setCurrentRecord({ ...record });
    setShowEditModal(true);
  };

  // Handle saving edited record
  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      await api.put(`/api/csv-data/${currentRecord.id}/`, currentRecord);
      setShowEditModal(false);
      fetchData(); // Refresh data after edit
      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error saving record:", err);
      setError(
        `Failed to save changes. ${err.response?.data?.detail || err.message}`
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle changes to the edit form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRecord({ ...currentRecord, [name]: value });
  };

  // Function to safely render HTML content
  const renderHTML = (htmlContent) => {
    return { __html: htmlContent || "" };
  };

  // Delete record handler
  const handleDeleteClick = (record) => {
    setRecordToDelete(record);
    setShowDeleteModal(true);
  };

  // Execute delete
  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/api/csv-data/${recordToDelete.id}/`);
      setShowDeleteModal(false);
      fetchData(); // Refresh data after delete
      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error deleting record:", err);
      setError(
        `Failed to delete record. ${err.response?.data?.detail || err.message}`
      );
    } finally {
      setDeleting(false);
      setRecordToDelete(null);
    }
  };

  // Function to create preview of HTML content
  const createHTMLPreview = (html) => {
    if (!html) return <em>No content</em>;

    // Remove HTML tags for preview
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    let text = tempDiv.textContent || tempDiv.innerText || "";

    // Limit to 100 characters
    if (text.length > 100) {
      text = text.substring(0, 100) + "...";
    }

    return text || <em>No text content</em>;
  };

  // Pagination controls
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const items = [];
    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => setCurrentPage(currentPage - 1)}
      />
    );

    // First page
    items.push(
      <Pagination.Item
        key={1}
        active={currentPage === 1}
        onClick={() => setCurrentPage(1)}
      >
        1
      </Pagination.Item>
    );

    // Ellipsis if needed
    if (currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" />);
    }

    // Pages around current page
    for (
      let page = Math.max(2, currentPage - 1);
      page <= Math.min(totalPages - 1, currentPage + 1);
      page++
    ) {
      items.push(
        <Pagination.Item
          key={page}
          active={currentPage === page}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </Pagination.Item>
      );
    }

    // Ellipsis if needed
    if (currentPage < totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" />);
    }

    // Last page
    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => setCurrentPage(currentPage + 1)}
      />
    );

    return <Pagination>{items}</Pagination>;
  };

  return (
    <div>
      <Row className="mb-4">
        <Col md={4}>
          <Form.Group className="mb-3 mb-md-0">
            <Form.Label>
              <strong>Select a Subject to View Questions</strong>
            </Form.Label>
            <Form.Select
              value={selectedSubject}
              onChange={handleSubjectChange}
              disabled={loadingSubjects}
              className="form-control-lg"
            >
              <option value="">
                {loadingSubjects
                  ? "Loading subjects..."
                  : "-- Select a Subject --"}
              </option>
              {subjects.map((subject, index) => (
                <option
                  key={index}
                  value={subject.name}
                  disabled={subject.disabled}
                >
                  {subject.name}{" "}
                  {subject.count > 0
                    ? `(${subject.count} question${
                        subject.count !== 1 ? "s" : ""
                      })`
                    : ""}
                </option>
              ))}
            </Form.Select>
            {loadingSubjects && (
              <div className="text-center mt-2">
                <Spinner animation="border" size="sm" role="status">
                  <span className="visually-hidden">Loading subjects...</span>
                </Spinner>
                <span className="ms-2">Loading subjects...</span>
              </div>
            )}
          </Form.Group>
        </Col>
        <Col md={8}>
          <Form.Label>Search (optional)</Form.Label>
          <Form onSubmit={handleSearch}>
            <InputGroup>
              <Form.Control
                placeholder="Search by reference code, question or markscheme"
                value={searchTerm}
                onChange={handleSearchChange}
                disabled={!selectedSubject}
                onKeyDown={(e) => e.key === "Enter" && handleSearch(e)}
              />
              <Button
                variant="primary"
                type="submit"
                disabled={!selectedSubject}
              >
                Search
              </Button>
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                Clear All
              </Button>
            </InputGroup>
          </Form>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError("")} dismissible>
          {error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-3">
        <Badge bg="info" className="p-2">
          {totalItems > 0
            ? `Showing ${data.length} of ${totalItems} total questions for "${selectedSubject}"`
            : "No results found"}
        </Badge>
        <Button
          variant="secondary"
          onClick={fetchData}
          disabled={!selectedSubject}
        >
          Refresh Data
        </Button>
      </div>

      {!selectedSubject ? (
        <Card className="text-center p-5 my-5">
          <Card.Body>
            <Card.Title>No Subject Selected</Card.Title>
            <Card.Text>
              Please select a subject from the dropdown menu above to view
              questions.
            </Card.Text>
          </Card.Body>
        </Card>
      ) : loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading questions for {selectedSubject}...</p>
        </div>
      ) : data.length === 0 ? (
        <Card className="text-center p-4">
          <Card.Body>
            <Card.Title>No Questions Found</Card.Title>
            <Card.Text>
              {`No questions found for subject "${selectedSubject}"${
                searchTerm ? ` matching "${searchTerm}"` : ""
              }`}
            </Card.Text>
          </Card.Body>
        </Card>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th
                    style={{ cursor: "pointer" }}
                    onClick={() => handleSort("reference_code")}
                  >
                    Reference Code{getSortIndicator("reference_code")}
                  </th>
                  <th>Question</th>
                  <th>Mark Scheme</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id}>
                    <td>{item.reference_code || "-"}</td>
                    <td>{createHTMLPreview(item.question_body)}</td>
                    <td>{createHTMLPreview(item.markscheme_body)}</td>
                    <td className="text-center">
                      <Button
                        variant="primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteClick(item)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <Form.Group className="d-flex align-items-center">
              <Form.Label className="me-2 mb-0">Items per page:</Form.Label>
              <Form.Select
                style={{ width: "80px" }}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1); // Reset to first page when changing page size
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
            </Form.Group>
            {renderPagination()}
          </div>
        </>
      )}

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        size="xl"
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Question - {currentRecord.reference_code || ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Reference Code</Form.Label>
              <Form.Control
                type="text"
                name="reference_code"
                value={currentRecord.reference_code || ""}
                onChange={handleInputChange}
                readOnly
              />
              <Form.Text className="text-muted">
                Reference code cannot be changed
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Subject</Form.Label>
              <Form.Control
                type="text"
                name="subject"
                value={currentRecord.subject || ""}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Tabs
              defaultActiveKey="question"
              id="edit-question-tabs"
              className="mb-3"
            >
              <Tab eventKey="question" title="Question">
                <Row>
                  <Col md={6}>
                    <Card>
                      <Card.Header>HTML Editor</Card.Header>
                      <Card.Body>
                        <Form.Control
                          as="textarea"
                          rows={12}
                          name="question_body"
                          value={currentRecord.question_body || ""}
                          onChange={handleInputChange}
                          className="font-monospace"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card>
                      <Card.Header>Preview</Card.Header>
                      <Card.Body
                        className="overflow-auto"
                        style={{ maxHeight: "300px" }}
                      >
                        <div
                          dangerouslySetInnerHTML={renderHTML(
                            currentRecord.question_body
                          )}
                          className="question-preview"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>
              <Tab eventKey="markscheme" title="Mark Scheme">
                <Row>
                  <Col md={6}>
                    <Card>
                      <Card.Header>HTML Editor</Card.Header>
                      <Card.Body>
                        <Form.Control
                          as="textarea"
                          rows={12}
                          name="markscheme_body"
                          value={currentRecord.markscheme_body || ""}
                          onChange={handleInputChange}
                          className="font-monospace"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card>
                      <Card.Header>Preview</Card.Header>
                      <Card.Body
                        className="overflow-auto"
                        style={{ maxHeight: "300px" }}
                      >
                        <div
                          dangerouslySetInnerHTML={renderHTML(
                            currentRecord.markscheme_body
                          )}
                          className="markscheme-preview"
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Tab>
            </Tabs>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={saving}>
            {saving ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Saving...</span>
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete this question?</p>
          <p>
            <strong>Reference Code:</strong> {recordToDelete?.reference_code}
          </p>
          <Alert variant="danger">
            This action cannot be undone. The question will be permanently
            deleted.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
                <span className="ms-2">Deleting...</span>
              </>
            ) : (
              "Delete Question"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DataTable;
